import { InfiniteData } from '@tanstack/react-query';
import axios from 'axios';
import qs from 'qs';
import { isEmpty } from 'radash';
import { createInfiniteQuery, createMutation } from 'react-query-kit';

import { HTTP_HEADERS } from '@/constants';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { queryClient } from '@/services/query-client';
import { restUrl } from '@/services/utils';

import { ExportDataType } from './export.types';

interface ExportVariables {
    type?: string;
    status?: string;
    jobScheduleId?: number;
    offset?: number;
    limit?: number;
}

interface ExportResponse {
    data: ExportDataType[];
    cursor?: string;
}

const getExportVisibility = () => {
    try {
        const angularJsInjector = getInjector();
        const auth = angularJsInjector.get('Auth');
        const $rootScope = getRootScope(angularJsInjector);
        const currentUserId = $rootScope?.user?.userId ?? $rootScope?.userId;
        const canViewExportEntry = (entry: ExportDataType) => {
            if (auth?.hasAnyPermission?.('exports', 'list_for_all_users')) {
                return true;
            }
            if (!currentUserId) {
                return false;
            }
            return entry.creationUser === currentUserId;
        };
        return { canViewExportEntry };
    } catch (error) {
        return { canViewExportEntry: (_entry: ExportDataType) => true };
    }
};

const useExportData = createInfiniteQuery<ExportResponse, ExportVariables, Error, string | undefined>({
    queryKey: ['items/exports'],
    fetcher: async (variables, { pageParam }) => {
        const response = await axios.get(restUrl('items/exports?type=EXPORT'), {
            params: variables,
            headers: {
                [HTTP_HEADERS.X_MESSAGE_CURSOR]: pageParam
            },
            paramsSerializer: (params) => {
                // We need to remove empty params i.e. '', as it breaks the endpoint.
                const filteredParams = Object.keys(params)
                    .reduce((allParams: Partial<Record<string, any>>, currentParam: string) => {
                        const paramValue = params[currentParam];
                        if (paramValue && paramValue != '') {
                            allParams[currentParam] = paramValue;
                        }

                        return allParams;
                    }, []);

                return qs.stringify(filteredParams);
            }
        });

        const { canViewExportEntry } = getExportVisibility();

        return {
            data: (response.data ?? []).filter(canViewExportEntry),
            cursor: response.headers[HTTP_HEADERS.X_MESSAGE_CURSOR] ?? undefined
        };
    },
    getNextPageParam: ({ data, cursor }) => isEmpty(data) || isEmpty(cursor) ? null : cursor,
    initialPageParam: undefined
});

const useUpdateExportData = createMutation<void, { exportEntry: ExportDataType, cacheQueryVariables: Record<string,unknown> }, void>({
    mutationFn: async ({ exportEntry, cacheQueryVariables }: { exportEntry: ExportDataType, cacheQueryVariables: Record<string,unknown> }) => {
        const { canViewExportEntry } = getExportVisibility();

        queryClient.setQueryData(['items/exports', cacheQueryVariables], (oldExportState: InfiniteData<ExportResponse>) => {

            if (oldExportState.pages.length === 0) {
                return { ...oldExportState, pages: [ { data: [exportEntry], cursor: undefined } ] };
            }

            let found = false;
            const updatedPages = oldExportState.pages.map((oldPage) => {
                return {
                    ...oldPage,
                    data: oldPage.data.map(oldExportEntry => {
                        if (oldExportEntry.id === exportEntry.id) {
                            found = true;
                            return { ...oldExportEntry, ...exportEntry };
                        } else {
                            return { ...oldExportEntry };
                        }
                    })
                };
            });

            if (!found && canViewExportEntry(exportEntry)) {
                updatedPages[0].data.unshift(exportEntry);
            }

            return {
                pageParams: oldExportState.pageParams,
                pages: updatedPages
            };
        });
    }
});

export { useExportData, useUpdateExportData };
